import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import useSeo from "@/hooks/useSeo";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { Button } from "@/components/ui/button";
import { Check, X, ArrowRight, Sparkles } from "lucide-react";
import { PublicAmbient, KineticHeading, Reveal, DonkeyQuote } from "@/components/public/PublicVisuals";

const EASE = [0.22, 1, 0.36, 1];

const GENERIC_AI = [
  "Produces ideas from a short prompt.",
  "Often gives familiar, generic suggestions.",
  "Tends to agree rather than challenge.",
  "Does not properly understand the person's constraints.",
  "Leaves the user alone after generating the idea.",
];
const DONKEY = [
  "Interviews the person properly.",
  "Analyses their skills, experience, circumstances and ability to reach customers.",
  "Produces three genuinely different opportunities built around them.",
  "Challenges weak assumptions instead of simply agreeing.",
  "Recommends the strongest opportunity and explains why.",
  "Helps validate, brand, build, launch and grow the business afterward.",
];
const FOR_YOU = [
  "You want a business built around what you can genuinely do.",
  "You need direction, not another recycled list of side hustles.",
  "You want honest validation before spending serious time or money.",
  "You want help beyond the original idea.",
];
const NOT_FOR_YOU = [
  "You want overnight-rich promises.",
  "You expect passive income without doing the work.",
  "You only want an AI to tell you what you want to hear.",
  "You want hype rather than honest analysis.",
];

const AI_OUTPUT = ["Great idea!", "It's a growing market!", "Here are 10 ways to market it…"];
const DONKEY_Q = ["Who actually buys it?", "Why would they choose you?", "Where's the evidence of demand?", "Who's already competing nearby?", "Can you actually reach them?"];
const EXAMIN = [
  { t: "“Everyone drinks coffee”", verdict: "rejected", note: "Assumption, not evidence" },
  { t: "Local gap: 1 café per ~4,000 residents", verdict: "kept", note: "Evidence found" },
  { t: "Three offices reachable within 400m", verdict: "kept", note: "Reachable" },
];

// ===== SIGNATURE SCENE: the confrontation. Plays once when scrolled into view, then settles. =====
function Confrontation() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const [step, setStep] = useState(reduce ? 6 : 0);

  useEffect(() => {
    if (reduce || !inView) return;
    const ts = [250, 1500, 3200, 4100, 4900, 5600].map((ms, i) => setTimeout(() => setStep(i + 1), ms));
    return () => ts.forEach(clearTimeout);
  }, [inView, reduce]);

  const pose = step >= 6 ? "verdict" : step >= 3 ? "sceptical" : step >= 1 ? "thinking" : "idle";
  const poseLine = step >= 6 ? "Now we've got something worth testing." : step >= 3 ? "That's an assumption, not evidence." : "Let's see what the market actually says.";

  return (
    <section ref={ref} className="mt-16" data-testid="why-not-chatgpt">
      <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
      <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-donkey-warm">Watch the same idea go through both.</h2>

      {/* the idea enters */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 14 }}
        animate={step >= 1 || reduce ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, ease: EASE }}
        className="mt-8 mx-auto max-w-md rounded-full border border-white/15 bg-donkey-bg px-5 py-3 text-center text-donkey-warm"
      >
        <span className="text-xs uppercase tracking-wider text-donkey-grey mr-2">Idea</span>“I want to start a coffee business.”
      </motion.div>

      <div className="mt-8 grid md:grid-cols-2 gap-6 items-start">
        {/* GENERIC AI — agrees instantly */}
        <div className="rounded-2xl p-6 border border-white/10 bg-donkey-bg">
          <div className="flex items-center gap-2 mb-4 text-donkey-grey">
            <Sparkles className="w-4 h-4" /><h3 className="font-heading font-semibold">Generic AI</h3>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-donkey-grey/70">instant</span>
          </div>
          <div className="space-y-2.5 min-h-[7rem]">
            {AI_OUTPUT.map((a, i) => (
              <motion.div
                key={a}
                initial={reduce ? false : { opacity: 0, x: -14 }}
                animate={step >= 1 || reduce ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.3, delay: 0.1 + i * 0.18, ease: EASE }}
                className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-sm text-donkey-warm/85"
              >
                <Check className="w-4 h-4 text-donkey-grey/60 shrink-0" />{a}
              </motion.div>
            ))}
            <motion.p
              initial={reduce ? false : { opacity: 0 }}
              animate={step >= 2 || reduce ? { opacity: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="text-xs text-donkey-red/80 pt-1"
            >
              …but on whose evidence?
            </motion.p>
          </div>
        </div>

        {/* THE HONEST DONKEY — interrogates, then examines */}
        <div className="rounded-2xl p-6 border border-donkey-orange/30 bg-donkey-deep">
          <div className="flex items-center gap-2 mb-4">
            <img src={`/mascot/${pose}.png`} alt="" className="h-8 w-auto object-contain" />
            <h3 className="font-heading font-semibold text-donkey-orange">The Honest Donkey</h3>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-donkey-orange/70">interrogates</span>
          </div>
          <div className="space-y-2 min-h-[7rem]">
            {DONKEY_Q.map((q, i) => (
              <motion.div
                key={q}
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={step >= 1 || reduce ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.3, delay: 0.15 + i * 0.2, ease: EASE }}
                className="text-sm text-donkey-warm/90 border-l-2 border-donkey-orange/50 pl-3"
              >
                {q}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* assumption -> challenge -> evidence -> verdict */}
      <div className="mt-6 rounded-2xl border border-white/10 bg-donkey-bg p-6">
        <p className="text-[11px] uppercase tracking-wider text-donkey-grey mb-4">Then it tests each claim</p>
        <div className="space-y-2.5">
          {EXAMIN.map((e, i) => {
            const shown = step >= 3 + i;
            const rejected = e.verdict === "rejected";
            return (
              <motion.div
                key={i}
                initial={reduce ? false : { opacity: 0, x: 18 }}
                animate={shown || reduce ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, ease: EASE }}
                className={`flex items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 text-sm
                  ${rejected ? "border-donkey-red/40 bg-donkey-red/5 text-donkey-grey" : "border-donkey-green/40 bg-donkey-green/10 text-donkey-warm"}`}
              >
                <span className={rejected ? "line-through decoration-donkey-red/70" : ""}>{e.fix || e.t}</span>
                <span className={`inline-flex items-center gap-1.5 text-xs shrink-0 ${rejected ? "text-donkey-red" : "text-donkey-green"}`}>
                  {rejected ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}{e.note}
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* Donkey's conclusion */}
        <AnimatePresence>
          {(step >= 3 || reduce) && (
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="mt-5 flex items-center gap-3 border-t border-white/10 pt-4"
            >
              <img src={`/mascot/${pose}.png`} alt="" className="h-14 w-auto object-contain shrink-0 drop-shadow-[0_0_24px_rgba(249,115,22,0.3)]" />
              <p className={`text-sm italic ${step >= 6 ? "text-donkey-green" : "text-donkey-amber"}`}>“{poseLine}”</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <p className="mt-10 font-heading font-bold text-xl sm:text-2xl text-donkey-warm leading-snug border-l-2 border-donkey-orange pl-5">
        ChatGPT can give you a list. <span className="text-donkey-orange">The Honest Donkey helps you find something worth building—and build it properly.</span>
      </p>

      {/* at-a-glance comparison (all original copy preserved) */}
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <Reveal x={-32} className="h-full">
          <div className="rounded-2xl p-7 border border-white/10 bg-donkey-bg h-full">
            <h3 className="font-heading font-semibold text-lg text-donkey-grey mb-5">Generic AI</h3>
            <ul className="space-y-3.5">
              {GENERIC_AI.map((g) => (<li key={g} className="flex items-start gap-3 text-donkey-grey"><X className="w-5 h-5 text-donkey-red shrink-0 mt-0.5" /><span className="text-sm leading-relaxed">{g}</span></li>))}
            </ul>
          </div>
        </Reveal>
        <Reveal x={32} delay={0.12} className="h-full">
          <div className="rounded-2xl p-7 border border-donkey-orange/30 bg-donkey-deep h-full transition-all hover:border-donkey-orange/60 hover:shadow-[0_0_40px_rgba(249,115,22,0.15)]">
            <h3 className="font-heading font-semibold text-lg text-donkey-orange mb-5">The Honest Donkey</h3>
            <ul className="space-y-3.5">
              {DONKEY.map((d) => (<li key={d} className="flex items-start gap-3 text-donkey-warm"><Check className="w-5 h-5 text-donkey-green shrink-0 mt-0.5" /><span className="text-sm leading-relaxed">{d}</span></li>))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ===== Opening scene: the Donkey as the focal intelligence, environment carries the story. =====
function DonkeyOpening() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [step, setStep] = useState(reduce ? 5 : 0);

  useEffect(() => {
    if (reduce || !inView) return;
    const ts = [400, 1500, 2600, 3700, 4700].map((ms, i) => setTimeout(() => setStep(i + 1), ms));
    return () => ts.forEach(clearTimeout);
  }, [inView, reduce]);

  const rimOn = step >= 5;
  const chipBase = "absolute z-20 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider font-mono backdrop-blur-sm max-w-[48%]";
  const tone = {
    amber: "border-donkey-amber/50 text-donkey-amber bg-donkey-amber/10",
    red: "border-donkey-red/50 text-donkey-red bg-donkey-red/10",
    green: "border-donkey-green/50 text-donkey-green bg-donkey-green/10",
  };
  const chipAnim = (show) => ({
    initial: reduce ? false : { opacity: 0, y: 8, scale: 0.92 },
    animate: show ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0 },
    transition: { duration: 0.45, ease: EASE },
  });

  return (
    <div ref={ref} className="mt-8 grid lg:grid-cols-[1.3fr_1fr] gap-8 items-center" data-testid="donkey-opening">
      {/* LEFT — the explanation */}
      <div className="order-2 lg:order-1 space-y-5 text-donkey-grey leading-relaxed">
        <p>Because donkeys are famously sensible. They won't walk off a cliff no matter how enthusiastically you point at it. That is precisely the temperament missing from most business advice.</p>
        <p>The Honest Donkey exists because the internet is drowning in recycled side hustles and overnight-millionaire nonsense. We think you deserve better: opportunities built around what you actually know, who you can actually reach, and what could realistically stand in your way.</p>
        <p>We are sceptical of exaggerated claims and allergic to hype. We will tell you when an idea is promising, and we will tell you — politely — when it is not. Enthusiasm is not evidence, and a good offer is no use if you cannot reach the buyer.</p>
        <p className="text-donkey-warm">Tell the donkey what you know. Get business opportunities built around you.</p>
      </div>

      {/* RIGHT — the intelligence stage */}
      <div className="order-1 lg:order-2 relative w-full h-[330px] sm:h-[390px] lg:h-[500px] overflow-hidden rounded-3xl border border-white/10 bg-donkey-bg" data-testid="donkey-stage">
        {/* blueprint grid */}
        <div aria-hidden className="absolute inset-0 opacity-[0.1]" style={{ backgroundImage: "linear-gradient(rgba(249,115,22,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.3) 1px, transparent 1px)", backgroundSize: "34px 34px" }} />
        {/* rim glow behind donkey */}
        <motion.div aria-hidden className="absolute -right-8 bottom-4 w-[80%] h-[80%] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(249,115,22,0.3), transparent 66%)" }}
          initial={reduce ? false : { opacity: 0.45 }} animate={{ opacity: rimOn ? 1 : 0.5 }} transition={{ duration: 0.8, ease: EASE }} />
        {/* floor glow */}
        <div aria-hidden className="absolute bottom-0 right-0 left-1/3 h-12" style={{ background: "radial-gradient(60% 100% at 72% 100%, rgba(249,115,22,0.34), transparent 70%)" }} />

        {/* travelling evidence signals toward the Donkey (once, on evidence check) */}
        {!reduce && [0, 1].map((i) => (
          <motion.span key={i} aria-hidden className="absolute z-10 w-1.5 h-1.5 rounded-full bg-donkey-amber shadow-[0_0_10px_rgba(245,166,35,0.9)]"
            style={{ top: `${54 + i * 7}%`, left: "30%" }}
            initial={{ opacity: 0, x: 0 }}
            animate={step >= 4 ? { opacity: [0, 1, 1, 0], x: [0, 70, 100, 125] } : {}}
            transition={{ duration: 1.1, delay: 0.2 + i * 0.3, ease: EASE }} />
        ))}

        {/* reasoning log — fragments appear once, then settle */}
        <motion.div className={`${chipBase} ${tone.amber}`} style={{ top: "9%", left: "5%" }} {...chipAnim(step >= 1)}><span>Claim</span></motion.div>
        <motion.div className={`${chipBase} ${tone.amber}`} style={{ top: "25%", left: "5%" }} {...chipAnim(step >= 2)}><Sparkles className="w-3 h-3 shrink-0" /><span>Assumption?</span></motion.div>
        <motion.div className={`${chipBase} ${tone.red}`} style={{ top: "41%", left: "5%" }} {...chipAnim(step >= 3)}><X className="w-3 h-3 shrink-0" /><span className="line-through decoration-donkey-red/70">No evidence</span></motion.div>
        <motion.div className={`${chipBase} ${tone.green}`} style={{ top: "57%", left: "5%" }} {...chipAnim(step >= 4)}><Check className="w-3 h-3 shrink-0" /><span>Evidence</span></motion.div>
        <motion.div className={`${chipBase} ${tone.green} font-semibold shadow-[0_0_20px_rgba(74,222,128,0.25)]`} style={{ bottom: "9%", left: "6%" }} {...chipAnim(step >= 5)}><Check className="w-3 h-3 shrink-0" /><span>Worth testing</span></motion.div>

        {/* gold lock ring on verdict */}
        {!reduce && (
          <motion.div aria-hidden className="absolute right-3 bottom-3 w-[62%] h-[88%] rounded-full border border-donkey-amber/0 pointer-events-none"
            animate={step >= 5 ? { borderColor: ["rgba(245,166,35,0)", "rgba(245,166,35,0.55)", "rgba(245,166,35,0.2)"] } : {}}
            transition={{ duration: 1, ease: EASE }} />
        )}

        {/* THE DONKEY — high-res, transparent, static & sharp */}
        <img src="/donkey-hero.png" alt="The Honest Donkey"
          className="absolute bottom-0 right-1 lg:right-3 h-[84%] sm:h-[88%] lg:h-[94%] w-auto object-contain object-bottom donkey-alive drop-shadow-[0_0_44px_rgba(249,115,22,0.28)]"
          data-testid="donkey-opening-mascot" />
      </div>
    </div>
  );
}

// ===== Connector — a data path that leads the eye into the comparison below. =====
function ConnectorPath() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  return (
    <div ref={ref} className="relative h-14 flex justify-center" aria-hidden data-testid="section-connector">
      <div className="relative w-px h-full">
        <motion.div className="absolute inset-0 w-px bg-gradient-to-b from-donkey-orange via-donkey-amber to-donkey-orange/0"
          style={{ transformOrigin: "top" }}
          initial={reduce ? false : { scaleY: 0 }} animate={inView ? { scaleY: 1 } : {}} transition={{ duration: 0.8, ease: EASE }} />
        {!reduce && (
          <motion.span className="absolute left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-donkey-amber shadow-[0_0_12px_rgba(245,166,35,0.9)]"
            initial={{ top: 0, opacity: 0 }} animate={inView ? { top: "100%", opacity: [0, 1, 1, 0] } : {}}
            transition={{ duration: 1, ease: EASE, delay: 0.2 }} />
        )}
      </div>
    </div>
  );
}

export default function About() {
  const navigate = useNavigate();
  useSeo({
    title: "Why the Donkey? — Honest business discovery, not another AI idea list",
    description: "ChatGPT can give you a list. The Honest Donkey interviews you properly, challenges weak assumptions and helps you find—and build—a business worth building. Evidence, not hype.",
    path: "/about",
  });

  return (
    <div className="relative overflow-hidden">
      <PublicAmbient intensity={0.5} />
      <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-4">Why the Donkey?</p>
        <KineticHeading text="Why a donkey?" className="text-4xl sm:text-5xl" />
        <DonkeyQuote className="mt-6 max-w-xl" quotes={["I won't walk off a cliff, no matter how hard you point.", "Enthusiasm is not evidence.", "I'll tell you when it's promising — and when it's not."]} />

        <DonkeyOpening />

        <ConnectorPath />

        <Confrontation />

        {/* Is the Donkey right for you? */}
        <section className="mt-20" data-testid="donkey-right-for-you">
          <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-donkey-warm">Is the Donkey right for you?</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl p-7 border border-donkey-orange/30 bg-donkey-deep">
              <h3 className="font-heading font-semibold text-lg text-donkey-warm mb-5">The Donkey is for you if:</h3>
              <ul className="space-y-3.5">
                {FOR_YOU.map((f) => (<li key={f} className="flex items-start gap-3 text-donkey-warm"><Check className="w-5 h-5 text-donkey-green shrink-0 mt-0.5" /><span className="text-sm leading-relaxed">{f}</span></li>))}
              </ul>
            </div>
            <div className="rounded-2xl p-7 border border-white/10 bg-donkey-bg">
              <h3 className="font-heading font-semibold text-lg text-donkey-grey mb-5">It isn't for you if:</h3>
              <ul className="space-y-3.5">
                {NOT_FOR_YOU.map((n) => (<li key={n} className="flex items-start gap-3 text-donkey-grey"><X className="w-5 h-5 text-donkey-red shrink-0 mt-0.5" /><span className="text-sm leading-relaxed">{n}</span></li>))}
              </ul>
            </div>
          </div>
        </section>

        <section className="mt-16 text-center card-donkey rounded-2xl p-10">
          <DonkeyMascot size={96} alt="" className="mx-auto mb-5 opacity-95 drop-shadow-[0_0_40px_rgba(249,115,22,0.22)]" />
          <Button onClick={() => navigate("/pricing")} className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 h-12" data-testid="about-prove-cta">
            Start with Prove — £29 <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="mt-4 text-sm text-donkey-grey">£29 per month. Cancel anytime securely through Stripe.</p>
        </section>
      </div>
    </div>
  );
}
